/**
 * Shared TanStack Query defaults.
 *
 * The library itself never fetches — components take rows and state as props
 * (CLAUDE.md §11's screen contracts are server-driven, not component-driven).
 * What is shared is the *policy*: how long ERP data stays fresh, and the rule
 * that a request rejected for what it contained must never be retried. Three
 * apps disagreeing about that would produce three different behaviours on the
 * same 422.
 */

import { QueryClient, type QueryClientConfig } from '@tanstack/vue-query'

import { isRetryableProblem, toProblemDetails } from './problem'

export interface QueryDefaultsOptions {
  /**
   * How long a list or document stays fresh. ERP data changes on human
   * timescales, so 30s avoids a refetch storm without showing stale ledgers.
   */
  staleTimeMs?: number
  /** Attempts after the first for retryable failures. */
  retries?: number
}

/** The retry predicate: never re-send a request the server refused on merit. */
export function shouldRetry(failureCount: number, error: unknown, retries = 2): boolean {
  if (failureCount >= retries) return false
  return isRetryableProblem(error)
}

export function createQueryClient(options: QueryDefaultsOptions = {}): QueryClient {
  const { staleTimeMs = 30_000, retries = 2 } = options

  const config: QueryClientConfig = {
    defaultOptions: {
      queries: {
        staleTime: staleTimeMs,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // An approver switching tabs should not fire a burst of refetches, but
        // a reconnected laptop should catch up.
        refetchOnReconnect: true,
        retry: (failureCount, error) => shouldRetry(failureCount, error, retries),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Writes carry an Idempotency-Key (§7), so a retry is safe — but only
        // when the failure was transport-level, never when it was a 422.
        retry: (failureCount, error) => shouldRetry(failureCount, error, 1),
      },
    },
  }

  return new QueryClient(config)
}

/** Query-key helpers so three apps do not invent three cache-key conventions. */
export const queryKeys = {
  list: (entity: string, params?: Record<string, unknown>) =>
    params ? ([entity, 'list', params] as const) : ([entity, 'list'] as const),
  detail: (entity: string, id: string | number) => [entity, 'detail', String(id)] as const,
  lookup: (entity: string, term: string) => [entity, 'lookup', term] as const,
} as const

/** Status code of a failed query, for error surfaces that branch on it. */
export function statusOf(error: unknown): number | null {
  return toProblemDetails(error)?.status ?? null
}
